import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

import {
  __TYPE__,
  BusinessNotification,
  BusinessObject,
  Customer,
  DATE_FORMAT,
  EquipmentResource,
  GoogleCalendarConnection,
  MODIFICATION_ACTION,
  Order,
  OrderItem,
  Product,
  ProductCategory,
  ServiceTag,
  SpecialistModification,
  Staff,
  TwAuth,
  TwEmployee,
  TwOfferGroup,
  TwProAuth,
  TwProEmployee,
  TwProTreatment,
  TwProVenue,
  TwVenue,
} from '../../models';
import {
  ArrayUtils,
  BusinessObjectUtils,
  DateTimeUtils,
  UrlUtils,
} from '../../utils';
import { BaseComponent } from '../../core';
import { ServerCallerService } from '../server-caller/server-caller.service';
import { ExchangeTokenServerCallerService } from '../server-caller/exchange-token-server-caller.service';
import { PrivateServerCallerService } from '../server-caller/private-server-caller.service';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class BusinessObjectService extends BaseComponent {
  constructor(
    private serverCaller: ServerCallerService,
    private privateServerCallerService: PrivateServerCallerService,
    private exchangeTokenServerCallerService: ExchangeTokenServerCallerService
  ) {
    super();
  }

  // region Business Object

  public async findBusinessObjectByNearBy(
    placeId: string,
    radius = 2,
    tag: string = null,
    date: string = null
  ): Promise<BusinessObject[]> {
    const url = UrlUtils.addParameterToUrl(`/v1/business-objects/near-by`, {
      placeId,
      radius,
      tag,
      date,
    });

    const response = await this.serverCaller.get(url);
    return response?.body?.map((bo: BusinessObject) =>
      BusinessObjectUtils.transformBoForFrontend(bo, true)
    );
  }

  public async getPublicBusinessObjectById(
    id: number
  ): Promise<BusinessObject> {
    const url = `/v1/business-object/${id}`;

    const response = await this.serverCaller.get(url);
    return BusinessObjectUtils.transformBoForFrontend(response?.body, true);
  }

  public async getBusinessObjectById(
    businessObjectId: number
  ): Promise<BusinessObject> {
    const url = `/v1/business-object/${businessObjectId}`;

    const response = await this.privateServerCallerService.get(url);
    return BusinessObjectUtils.transformBoForFrontend(response?.body, false);
  }

  public async getAllBusinessObject(): Promise<BusinessObject[]> {
    const url = `/v1/business-objects`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body?.map((bo) =>
      BusinessObjectUtils.transformBoForFrontend(bo, false)
    );
  }

  public async getVisitedBusinessObjects(): Promise<BusinessObject[]> {
    const url = `/v2/visited-business-objects`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body
      ?.map((bo) => BusinessObjectUtils.transformBoForFrontend(bo, true))
      ?.filter((bo) => !!bo.urlName?.length);
  }

  public async getBusinessObjectByUrlName(
    urlName: string
  ): Promise<BusinessObject> {
    const url = `/v1/business-object/url-name/${urlName}`;

    const response = await this.serverCaller.get(url);
    return BusinessObjectUtils.transformBoForFrontend(response?.body, true);
  }

  public async getBusinessObjectByQrCode(
    qrCode: string
  ): Promise<BusinessObject> {
    const url = `/v1/business-object/qr-code/${qrCode}`;

    const response = await this.serverCaller.get(url);
    return BusinessObjectUtils.transformBoForFrontend(response?.body, true);
  }

  public async updateBusinessObjectById(
    businessObject: BusinessObject
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObject.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      BusinessObjectUtils.transformBoForBackend(businessObject)
    );
    return response?.status === 200;
  }

  public async createCheckinQrCode(businessObjectId: number): Promise<string> {
    const url = `/v1/business-object/${businessObjectId}/qr-code`;

    const response = await this.privateServerCallerService.post(
      url,
      null,
      null,
      ServerCallerService.RESPONSE_TYPE.TEXT
    );
    return response?.body;
  }

  // endregion

  // region Category

  public async getProductCategoryById(
    businessObjectId: number,
    categoryId: number
  ): Promise<ProductCategory> {
    const url = `/v1/business-object/${businessObjectId}/category/${categoryId}`;

    const response = await this.privateServerCallerService.get(url);
    return BusinessObjectUtils.transformCategoryForFrontend(
      response?.body,
      false
    );
  }

  public async createProductCategory(
    businessObjectId: number,
    category: ProductCategory
  ): Promise<ProductCategory> {
    const url = `/v1/business-object/${businessObjectId}/category`;

    const response = await this.privateServerCallerService.post(
      url,
      BusinessObjectUtils.transformCategoryForBackend(category)
    );
    return response?.body;
  }

  public async updateProductCategoryById(
    businessObjectId: number,
    category: ProductCategory
  ): Promise<any> {
    const url = `/v1/business-object/${businessObjectId}/category/${category.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      BusinessObjectUtils.transformCategoryForBackend(category)
    );
    return response?.status;
  }

  public async deleteProductCategoryById(
    businessObjectId: number,
    categoryId: number
  ): Promise<any> {
    const url = `/v1/business-object/${businessObjectId}/category/${categoryId}`;

    const response = await this.privateServerCallerService.delete(url);
    return response?.status;
  }

  // endregion

  // region Product

  public async getProductById(
    businessObjectId: number,
    categoryId: number,
    productId: number
  ): Promise<Product> {
    const url = `/v1/business-object/${businessObjectId}/category/${categoryId}/product/${productId}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async createProductInDefaultCategory(
    businessObjectId: number,
    product: Product
  ): Promise<Product> {
    const url = `/v1/business-object/${businessObjectId}/product`;

    const response = await this.privateServerCallerService.post(
      url,
      BusinessObjectUtils.transformProductForBackend(product)
    );
    return response?.body;
  }

  public async createProductInSpecificCategory(
    businessObjectId: number,
    categoryId: number,
    product: Product
  ): Promise<Product> {
    const url = `/v1/business-object/${businessObjectId}/category/${categoryId}/product`;

    const response = await this.privateServerCallerService.post(
      url,
      BusinessObjectUtils.transformProductForBackend(product)
    );
    return response?.body;
  }

  public async updateProductById(
    businessObjectId: number,
    product: Product
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/product/${product.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      BusinessObjectUtils.transformProductForBackend(product)
    );
    return response?.status === 200;
  }

  public async updateAndMoveProductById(
    businessObjectId: number,
    categoryId: number,
    product: Product
  ): Promise<any> {
    const url = `/v1/business-object/${businessObjectId}/category/${categoryId}/product/${product.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      BusinessObjectUtils.transformProductForBackend(product)
    );
    return response?.status;
  }

  public async deleteProductById(
    businessObjectId: number,
    productId: number
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/product/${productId}`;

    const response = await this.privateServerCallerService.delete(url);
    return response?.status === 200;
  }

  public async updateSpecialistForProduct(
    businessObjectId: number,
    productId: number,
    specialistModification: SpecialistModification
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/product/${productId}/specialists`;

    const response = await this.privateServerCallerService.patch(
      url,
      specialistModification
    );

    return response?.status === 200;
  }

  public generateActionAndUpdateSpecialistForProduct(
    businessObjectId: number,
    originalProduct: Product,
    newSpecialListId: number[],
    staffs: Staff[]
  ): Promise<any[]> {
    let listOfStaffToAdd = newSpecialListId?.filter(
      (staffId) => !originalProduct?.specialists?.includes(staffId)
    );

    let listOfStaffToRemove = originalProduct?.specialists?.filter(
      (staffId) => !newSpecialListId?.includes(staffId)
    );

    if (
      ArrayUtils.isSameArray(
        newSpecialListId,
        staffs?.map((s) => s.id)
      )
    ) {
      // product can be done by all staffs => remove all specialist
      listOfStaffToAdd = [];
      listOfStaffToRemove = originalProduct?.specialists || [];
    }

    const updatePromises = [];

    if (listOfStaffToAdd?.length) {
      updatePromises.push(
        this.updateSpecialistForProduct(businessObjectId, originalProduct.id, {
          action: MODIFICATION_ACTION.ADD,
          specialists: listOfStaffToAdd,
        })
      );
    }

    if (listOfStaffToRemove?.length) {
      updatePromises.push(
        this.updateSpecialistForProduct(businessObjectId, originalProduct.id, {
          action: MODIFICATION_ACTION.REMOVE,
          specialists: listOfStaffToRemove,
        })
      );
    }

    return Promise.all(updatePromises);
  }

  // endregion

  // region Equipment

  public async createNewEquipment(
    businessObjectId: number,
    equipment: EquipmentResource
  ): Promise<EquipmentResource> {
    const url = `/v1/business-object/${businessObjectId}/equipment`;

    const response = await this.privateServerCallerService.post(url, equipment);
    return response?.body;
  }

  public async fetchAllEquipmentsOfBo(
    businessObjectId: number
  ): Promise<EquipmentResource[]> {
    const url = `/v1/business-object/${businessObjectId}/equipments`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async updateEquipmentResourceForProducts(
    businessObjectId: number,
    equipmentId: number,
    productIdList: number[]
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/equipment/${equipmentId}`;

    const response = await this.privateServerCallerService.put(
      url,
      productIdList
    );
    return response?.status === 200;
  }

  public async updateEquipmentResource(
    businessObjectId: number,
    equipment: EquipmentResource
  ): Promise<EquipmentResource> {
    const url = `/v1/business-object/${businessObjectId}/equipment`;

    const response = await this.privateServerCallerService.put(url, equipment);
    return response?.body;
  }

  public async deleteEquipmentResource(
    businessObjectId: number,
    equipmentId: number
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/equipment/${equipmentId}`;

    const response = await this.privateServerCallerService.delete(url);
    return response?.status === 200;
  }

  // endregion

  // region Notifications
  public async fetchAllNotifications(
    boId: number,
    fromDate: Date
  ): Promise<BusinessNotification[]> {
    const fromDateAsStr = DateTimeUtils.formatDateToString(
      fromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const url = `/v1/business-object/${boId}/notification?from=${fromDateAsStr}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body || [];
  }

  public async markNotificationAsRead(
    boId: number,
    notificationId: number
  ): Promise<boolean> {
    const url = `/v1/business-object/${boId}/notification/${notificationId}`;

    const response = await this.privateServerCallerService.put(url, {});

    return response?.status === 200;
  }

  public async markAllNotificationsAsRead(
    boId: number,
    notificationIds: number[]
  ): Promise<boolean> {
    return Promise.all(
      notificationIds?.map((id) => this.markNotificationAsRead(boId, id))
    )
      .then(() => true)
      .catch(() => false);
  }

  // endregion

  // region Teaser Image

  public async uploadTeaserImage(
    businessObjectId: number,
    file: File
  ): Promise<string> {
    const url = `/v1/business-object/${businessObjectId}/teaser`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await this.privateServerCallerService.post(
      url,
      formData,
      new HttpHeaders(),
      ServerCallerService.RESPONSE_TYPE.TEXT
    );

    return response?.body;
  }

  public async deleteTeaserImageOfBusinessObject(
    businessObjectId: number,
    teaserId: number
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/teaser/${teaserId}`;

    const response = await this.privateServerCallerService.delete(url);
    return response?.status === 200;
  }

  // endregion

  // region Service tags

  public async getAllServiceTags(): Promise<ServiceTag[]> {
    const url = `/v1/service-tags`;

    const response = await this.serverCaller.get(url);
    return response?.body?.map((t: ServiceTag) =>
      BusinessObjectUtils.transformServiceTagForFrontend(t)
    );
  }

  // endregion

  // region Checkin, Order

  public async checkinAndCreateOrderAsCustomer(
    boId: number,
    appointmentId?: number,
    products?: Product[]
  ): Promise<Order> {
    const isSuccess = await this.checkinCurrentLoggedInCustomer(
      boId,
      appointmentId
    );

    if (!isSuccess) {
      return null;
    }

    if (appointmentId) {
      return this.placeOrderForAppointmentAsCustomer(boId, appointmentId);
    } else {
      return this.placeOrderAsCustomer(
        boId,
        this.generateOrderToPlace(products)
      );
    }
  }

  public async checkinAndCreateOrderForCustomer(
    boId: number,
    customer: Customer,
    appointmentId?: number,
    products?: Product[]
  ): Promise<Order> {
    const isSuccess = await this.checkinCustomer(boId, customer, appointmentId);

    if (!isSuccess) {
      return null;
    }

    if (appointmentId) {
      return this.placeOrderForAppointment(boId, customer.id, appointmentId);
    } else {
      return this.placeOrder(
        boId,
        customer?.id,
        this.generateOrderToPlace(products)
      );
    }
  }

  private generateOrderToPlace(products?: Product[]): Order {
    if (!products?.length) {
      return {};
    }

    const orderItems: OrderItem[] = products.map((product) => ({
      __type__: __TYPE__.BASIC_BASE_ORDER_ITEM,
      amount: 1,
      productId: product.id,
      explicitSetPrice: BusinessObjectUtils.getPromotionalPrice(
        product,
        new Date()
      ),
    }));

    return {
      items: orderItems,
    };
  }

  private async placeOrder(
    boId: number,
    customerId: number,
    order: Order
  ): Promise<Order> {
    const url = `/v1/business-object/${boId}/customer/${customerId}/order`;

    return this.placeOrderWithAutoRetry(url, {
      ...order,
      __type__: __TYPE__.BASIC_BASE_ORDER,
    } as Order);
  }

  private async placeOrderAsCustomer(
    boId: number,
    order: Order
  ): Promise<Order> {
    const uuidAuthResponse =
      await this.exchangeTokenServerCallerService.getUuidAuthResponse(boId);

    const url = `/v1/business-object/${boId}/customer/${uuidAuthResponse.parameters.CustomerId}/order`;

    return this.placeOrderWithAutoRetryAsCustomer(boId, url, {
      ...order,
      __type__: __TYPE__.BASIC_BASE_ORDER,
    } as Order);
  }

  private async checkinCurrentLoggedInCustomer(
    boId: number,
    appointmentId?: number
  ): Promise<boolean> {
    const url = UrlUtils.addParameterToUrl(
      `/v1/business-object/${boId}/customer/check-in`,
      {
        appointmentId,
      }
    );
    const response = await this.exchangeTokenServerCallerService.post(
      boId,
      url,
      null
    );
    return !!response?.body?.id;
  }

  private async checkinCustomer(
    boId: number,
    customer: Customer,
    appointmentId?: number
  ): Promise<boolean> {
    const url = UrlUtils.addParameterToUrl(
      `/v1/business-object/${boId}/customer/${customer.phoneNr}/check-in`,
      { appointmentId }
    );

    const response = await this.privateServerCallerService.post(url, null);

    return !!response?.body?.id;
  }

  private async placeOrderForAppointmentAsCustomer(
    boId: number,
    appointmentId: number
  ): Promise<Order> {
    const uuidAuthResponse =
      await this.exchangeTokenServerCallerService.getUuidAuthResponse(boId);

    const url = `/v1/business-object/${boId}/customer/${uuidAuthResponse.parameters.CustomerId}/appointment/${appointmentId}/order`;

    return this.placeOrderWithAutoRetryAsCustomer(boId, url, null as Order);
  }

  private async placeOrderForAppointment(
    boId: number,
    customerId: number,
    appointmentId: number
  ): Promise<Order> {
    const url = `/v1/business-object/${boId}/customer/${customerId}/appointment/${appointmentId}/order`;

    return this.placeOrderWithAutoRetry(url, null as Order);
  }

  private async placeOrderWithAutoRetryAsCustomer(
    boId: number,
    url: string,
    data?: Order
  ): Promise<Order> {
    const numberOfRetry = 10;
    const timeOut = 200; // ms

    let retryCounter = 0;
    let order: Order;
    do {
      await of('')
        .pipe(takeUntil(this.componentDestroyed), delay(timeOut))
        .toPromise();

      const response = await this.exchangeTokenServerCallerService.post(
        boId,
        url,
        data
      );
      order = response?.body;

      if (!order?.id) {
        retryCounter += 1;
      } else {
        break;
      }
    } while (retryCounter < numberOfRetry);

    return order;
  }

  private async placeOrderWithAutoRetry(
    url: string,
    data?: Order
  ): Promise<Order> {
    const numberOfRetry = 10;
    const timeOut = 200; // ms

    let retryCounter = 0;
    let order: Order;
    do {
      await of('')
        .pipe(takeUntil(this.componentDestroyed), delay(timeOut))
        .toPromise();

      const response = await this.privateServerCallerService.post(url, data);
      order = response?.body;

      if (!order?.id) {
        retryCounter += 1;
      } else {
        break;
      }
    } while (retryCounter < numberOfRetry);

    return order;
  }

  // endregion

  // region Treatwell Pro

  public async loginToTwPro(
    businessObjectId: number,
    treatwellAuth: TwProAuth
  ): Promise<TwProVenue[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-pro`;

    const response = await this.privateServerCallerService.post(
      url,
      treatwellAuth
    );

    return response?.body;
  }

  public async getTwProVenues(businessObjectId: number): Promise<TwProVenue[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-pro-venues`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body;
  }

  public async getTwProMenus(
    businessObjectId: number
  ): Promise<TwProTreatment[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-pro-treatments`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body;
  }

  public async getTwProEmployees(
    businessObjectId: number
  ): Promise<TwProEmployee[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-pro-staffmembers`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body;
  }

  // endregion

  // region TW

  public async loginToTw(
    businessObjectId: number,
    treatwellAuth: TwAuth
  ): Promise<TwVenue[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-connect`;

    const response = await this.privateServerCallerService.post(
      url,
      treatwellAuth
    );

    return response?.body?.venues;
  }

  public async getTwVenues(businessObjectId: number): Promise<TwVenue[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-venues`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body;
  }

  public async getTwMenus(businessObjectId: number): Promise<TwOfferGroup[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-menu`;

    const response = await this.privateServerCallerService.get(url);

    return BusinessObjectUtils.formatTwMenuForFrontend(response?.body);
  }

  public async getTwEmployees(businessObjectId: number): Promise<TwEmployee[]> {
    const url = `/v1/business-object/${businessObjectId}/tw-employees`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body?.employees;
  }

  // endregion

  // region Google Calendar connection

  public async fetchGoogleCalendarConnection(
    businessObjectId: number
  ): Promise<GoogleCalendarConnection> {
    const url = `/v1/business-object/${businessObjectId}/google-calendar-connect`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async createGoogleCalendarConnection(
    businessObjectId: number,
    refreshToken: string
  ): Promise<GoogleCalendarConnection> {
    const url = `/v1/business-object/${businessObjectId}/google-calendar-connect`;

    const response = await this.privateServerCallerService.post(url, {
      refreshToken,
    });
    return response?.body;
  }

  public async toggleGoogleCalendarConnectionOnOff(
    businessObjectId: number
  ): Promise<GoogleCalendarConnection> {
    const url = `/v1/business-object/${businessObjectId}/google-calendar-sync-on-off`;

    const response = await this.privateServerCallerService.post(url, null);
    return response?.body;
  }

  // endregion
}
